import type { ProductColor } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { ContentBlock } from '../../../components/Analysis/ColumnComponents/ContentBlock.tsx';
import {
  selectArvFilteredCompSequence,
  selectDepreciateSeparately,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsById } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectDepreciationSegments } from '../../../redux/features/selectors/selectDepreciationSegments.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import type { AivArvActiveTab, RootState } from '../../../redux/types.ts';
import { EffectiveAgeSliderInputs } from '../../analysis/ColumnSections/EffectiveAgeSlider/EffectiveAgeSliderInputs.tsx';
import { QualitySlider } from '../../analysis/ColumnSections/QualitySlider.tsx';
import type { ViewMode } from '../types.ts';
import { CostNewBreakdown } from './CostNewBreakdown.tsx';
import { DepreciationBreakdown } from './DepreciationBreakdown.tsx';
import { ImprovementsTotal } from './ImprovementsTotal.tsx';
import { SiteSummary } from './SiteSummary.tsx';

const Section = ({
  backgroundColor,
  topBorder,
  children,
}: {
  backgroundColor: ProductColor;
  topBorder: boolean;
  children: ReactNode;
}) => (
  <Block
    backgroundColor={backgroundColor}
    paddingX="1rem"
    width="100%"
    borderTopWidth={topBorder ? '2px' : '0'}
    borderStyle="solid"
    borderColor="strokeLight"
  >
    {children}
  </Block>
);

const EffectiveAgeMain = ({ id }: { id: string }) => {
  const propertyType = useAppSelector(
    (state: RootState) => selectImprovementsById(state, id)?.propertyType,
  );
  const defaultEcoLifeField =
    propertyType === 'manufacturedHome' ? 'defaultEcoLifeManufactured' : 'defaultEcoLife';

  return (
    <ContentBlock>
      <EffectiveAgeSliderInputs
        id={id}
        index={0}
        effectiveAgeField="effectiveAge"
        ecoLifeField="totalEconomicLife"
        defaultEcoLifeField={defaultEcoLifeField}
        label="Effective Age"
        labelColor="buttonActive"
        enabled
      />
    </ContentBlock>
  );
};

const NoFeaturePlaceholder = ({ label }: { label: string }) => (
  <ContentBlock>
    <Flex justifyContent="space-between" alignItems="center" minHeight="56px">
      <Typography variant="h4" color="textDisabled">
        {label}
      </Typography>
      <Block />
    </Flex>
  </ContentBlock>
);

const SeparateDepreciationFeatures = ({
  id,
  presenceIds,
}: {
  id: string;
  presenceIds: string[];
}) => {
  const segments = useAppSelector((state: RootState) => selectDepreciationSegments(state, id));
  const presenceSegments = useAppSelector((state: RootState) =>
    presenceIds.map((pid) => selectDepreciationSegments(state, pid)),
  );

  const globallyEnabledByField = useMemo(() => {
    const map = new Map<string, boolean>();
    presenceSegments.forEach((segs) => {
      segs.forEach((seg) => {
        if (seg.enabled) map.set(seg.effectiveAgeField, true);
        else if (!map.has(seg.effectiveAgeField)) map.set(seg.effectiveAgeField, false);
      });
    });
    return map;
  }, [presenceSegments]);

  const orderedSegments =
    (presenceSegments.find((s) => s.length > 0) ?? segments).filter(
      (s) => globallyEnabledByField.get(s.effectiveAgeField) === true,
    ) ?? [];

  if (orderedSegments.length === 0) return null;

  const enabledByField = new Map(segments.map((s) => [s.effectiveAgeField, Boolean(s.enabled)]));

  return (
    <Flex flexDirection="column" gap="0.75rem">
      {orderedSegments.map((segment, idx) => {
        const labelBase = (segment.label ?? '').trim();
        const enabled = enabledByField.get(segment.effectiveAgeField) === true;

        if (!enabled) {
          const placeholderLabel = labelBase.length > 0 ? `No ${labelBase}` : '';
          return (
            <NoFeaturePlaceholder
              key={`${id}-${segment.effectiveAgeField}`}
              label={placeholderLabel}
            />
          );
        }

        const label = labelBase.length > 0 ? `${labelBase} Age` : '';

        return (
          <ContentBlock key={`${id}-${segment.effectiveAgeField}`}>
            <EffectiveAgeSliderInputs
              id={id}
              index={idx + 1}
              effectiveAgeField={segment.effectiveAgeField}
              ecoLifeField={segment.ecoLifeField}
              defaultEcoLifeField={segment.defaultEcoLifeField}
              label={label}
              labelColor="buttonActive"
              enabled
            />
          </ContentBlock>
        );
      })}
    </Flex>
  );
};

export const useSlidersByID = (viewMode: ViewMode, aivArvTab?: AivArvActiveTab) =>
  useMemo(
    () =>
      ({ id }: { id: string }) => {
        const depreciateSeparately = useAppSelector(selectDepreciateSeparately);
        const filteredCompSequence = useAppSelector(selectArvFilteredCompSequence);
        const subjectIds: string[] = [];
        if (aivArvTab === 'all') {
          subjectIds.push('subject', 'subjectArv');
        } else if (aivArvTab === 'arv') {
          subjectIds.push('subjectArv');
        } else {
          subjectIds.push('subject');
        }
        const featurePresenceIds = [...subjectIds, ...filteredCompSequence];

        return (
          <Flex flexDirection="column" gap="0">
            <Section backgroundColor="surface2" topBorder={false}>
              <Block className="appraisify-cost-quality" width="100%">
                <QualitySlider id={id} headingColor="buttonActive" />
              </Block>
            </Section>
            {viewMode === 'detailed' && (
              <Section backgroundColor="surface1" topBorder>
                <CostNewBreakdown id={id} featurePresenceIds={featurePresenceIds} />
              </Section>
            )}
            <Section backgroundColor="surface2" topBorder>
              <EffectiveAgeMain id={id} />
              {viewMode === 'detailed' && depreciateSeparately && (
                <Block marginTop="0.75rem">
                  <SeparateDepreciationFeatures id={id} presenceIds={featurePresenceIds} />
                </Block>
              )}
              {viewMode === 'detailed' && (
                <Block marginTop="0.5rem">
                  <DepreciationBreakdown id={id} />
                </Block>
              )}
            </Section>
            {viewMode === 'detailed' && (
              <Section backgroundColor="surface1" topBorder>
                <ImprovementsTotal id={id} />
              </Section>
            )}
            {viewMode === 'detailed' && (
              <Section backgroundColor="surface1" topBorder>
                <SiteSummary id={id} />
              </Section>
            )}
          </Flex>
        );
      },
    [aivArvTab, viewMode],
  );
